import {InputNumber} from "primereact/inputnumber";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";

import {formatParameterValue} from "@reva/shared/number-format";

import {useRevaTranslation} from "../../hooks/languageHooks";
import {selectAssociatedOutputParamOutputCurried} from "../../redux/objectParametersSelectors";
import {ALL_SOURCES_TO_ROUTE} from "../../redux/objectParametersSlice";
import ParameterFieldContainer from "./ParameterFieldContainer";
import {getResourceColor, useInputParameterDef} from "./paramHooks";

/**
 * Input for number parameters (Integer or Double).
 * @param {string} [parameter] parameter key (can be omitted, if parameter_details_def with parameter property is passed)
 * @param {RevaValueParameterType} [parameterType] type of parameter e.g. "AVM","META" or "DOC_PROPERTY"
 * @param {string|null} [label] rendered label (override from parameter details)
 * @param {string} id ID of the input
 * @param {InputParameterDetailsDef|null} [parameter_details_def] optional parameter details definition, to use instead of parameter
 * @param {string|null} [associatedOutputParam] optional output parameter name, to display the value of the output parameter
 * @param {string} originTab tab where the input can be edited
 * @return {React.JSX.Element|null}
 */
export default function ParameterInputArrayDisabled({
    parameter,
    parameterType,
    label,
    id,
    parameter_details_def,
    associatedOutputParam,
    originTab,
    onClick,
}) {
    const {t, language} = useRevaTranslation();
    const navigate = useNavigate();
    const parameterData = useInputParameterDef(parameter, parameter_details_def);
    const associatedOutputParamValue = useSelector(selectAssociatedOutputParamOutputCurried(associatedOutputParam));
    const fmtNumber = (value) => formatParameterValue(language, parameterData, value);
    const placeholder = associatedOutputParamValue
        ? `${t("object.associatedOutputParamPrefix")} ${fmtNumber(associatedOutputParamValue)}`
        : "";

    if (!parameterData) return null;
    const isDouble = parameterData.type === "double[]";

    return (
        <ParameterFieldContainer
            id={id}
            label={label}
            parameter={parameter}
            parameterType={parameterType}
            parameter_details_def={parameter_details_def}
            showUnit={true}
            showDescription={true}
            hasAssociatedOutputParam={!!associatedOutputParam}
            associatedOutputParamValue={associatedOutputParamValue}
            renderControl={({className, onChange, value, activeParameterType, hasConflict}) => {
                const color = getResourceColor(activeParameterType, hasConflict);
                const filledPlaceholderClass = associatedOutputParam ? "indication-filled-placeholder" : "";
                const aggValue = Array.isArray(value) ? value.reduce((acc, val) => acc + val, 0) : value;
                const tabLocation = ALL_SOURCES_TO_ROUTE[originTab];

                return (
                    <InputNumber
                        inputId={id}
                        className={"w-full cursor-pointer"}
                        disabled={true}
                        inputClassName={`text-right text-${color} ${className} ${filledPlaceholderClass} ${parameter}`}
                        value={aggValue}
                        useGrouping={true}
                        minFractionDigits={isDouble ? 2 : 0}
                        maxFractionDigits={isDouble ? 2 : 0}
                        locale={language}
                        onValueChange={onChange}
                        data-pr-position="top"
                        placeholder={placeholder}
                        onClick={onClick instanceof Function ? onClick : () => navigate(tabLocation)}
                    />
                );
            }}
        />
    );
}
